import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, ChevronDown, ChevronRight, Clock, Sparkles, CalendarDays, Loader2, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_COLORS = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-green-100 text-green-700 border-green-200'
};

const CurriculumManagement = () => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedSubject, setExpandedSubject] = useState(null);
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [topics, setTopics] = useState({});

  // Dialogs
  const [showSubjectDialog, setShowSubjectDialog] = useState(false);
  const [showTopicDialog, setShowTopicDialog] = useState(false);
  const [showSubtopicDialog, setShowSubtopicDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);

  // Edit states
  const [editingSubject, setEditingSubject] = useState(null);
  const [editingTopic, setEditingTopic] = useState(null);
  const [activeSubjectId, setActiveSubjectId] = useState(null);
  const [activeTopicId, setActiveTopicId] = useState(null);

  // Form states
  const [subjectForm, setSubjectForm] = useState({ name: '', year: new Date().getFullYear().toString(), grade_level: '' });
  const [topicForm, setTopicForm] = useState({ name: '', timeline_start: '', timeline_end: '', effort_hours: 0, priority: 'medium' });
  const [subtopicForm, setSubtopicForm] = useState({ name: '', timeline_start: '', timeline_end: '', effort_hours: 0, priority: 'medium' });

  // Schedule states
  const [scheduleForm, setScheduleForm] = useState({ grade_level: '', year: new Date().getFullYear().toString(), schedule: [] });
  const [schedules, setSchedules] = useState([]);

  // AI states
  const [aiLoading, setAiLoading] = useState(false);
  const [generatedSchedule, setGeneratedSchedule] = useState(null);
  const [showAiScheduleDialog, setShowAiScheduleDialog] = useState(false);

  // Subtopic status tracking
  const [subtopicStatusChanges, setSubtopicStatusChanges] = useState({});
  const [updatingStatus, setUpdatingStatus] = useState({});

  const fetchSubjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/teacher/curriculum/subjects`);
      setSubjects(res.data.subjects);
    } catch (err) {
      toast.error('Failed to load subjects');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTopics = useCallback(async (subjectId) => {
    try {
      const res = await axios.get(`${API}/teacher/curriculum/subjects/${subjectId}/topics`);
      setTopics(prev => ({ ...prev, [subjectId]: res.data.topics }));
    } catch (err) {
      toast.error('Failed to load topics');
    }
  }, []);

  const fetchSchedules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/teacher/schedule`);
      setSchedules(res.data.schedules);
    } catch (err) { /* silent */ }
  }, []);

  useEffect(() => { fetchSubjects(); fetchSchedules(); }, [fetchSubjects, fetchSchedules]);

  // --- Subject CRUD ---
  const handleSaveSubject = async () => {
    if (!subjectForm.name || !subjectForm.grade_level) { toast.error('Name and grade are required'); return; }
    try {
      if (editingSubject) {
        await axios.put(`${API}/teacher/curriculum/subjects/${editingSubject.id}`, subjectForm);
        toast.success('Subject updated');
      } else {
        await axios.post(`${API}/teacher/curriculum/subjects`, subjectForm);
        toast.success('Subject created');
      }
      setShowSubjectDialog(false);
      setEditingSubject(null);
      setSubjectForm({ name: '', year: new Date().getFullYear().toString(), grade_level: '' });
      fetchSubjects();
    } catch (err) { toast.error('Failed to save subject'); }
  };

  const handleDeleteSubject = async (id) => {
    if (!window.confirm('Delete this subject and all its topics?')) return;
    try {
      await axios.delete(`${API}/teacher/curriculum/subjects/${id}`);
      toast.success('Subject deleted');
      fetchSubjects();
    } catch (err) { toast.error('Failed to delete subject'); }
  };

  // --- Topic CRUD ---
  const handleSaveTopic = async () => {
    if (!topicForm.name) { toast.error('Topic name is required'); return; }
    try {
      if (editingTopic) {
        await axios.put(`${API}/teacher/curriculum/topics/${editingTopic.id}`, topicForm);
        toast.success('Topic updated');
      } else {
        await axios.post(`${API}/teacher/curriculum/subjects/${activeSubjectId}/topics`, topicForm);
        toast.success('Topic created');
      }
      setShowTopicDialog(false);
      setEditingTopic(null);
      setTopicForm({ name: '', timeline_start: '', timeline_end: '', effort_hours: 0, priority: 'medium' });
      fetchTopics(activeSubjectId);
      fetchSubjects();
    } catch (err) { toast.error('Failed to save topic'); }
  };

  const handleDeleteTopic = async (topicId, subjectId) => {
    if (!window.confirm('Delete this topic and subtopics?')) return;
    try {
      await axios.delete(`${API}/teacher/curriculum/topics/${topicId}`);
      toast.success('Topic deleted');
      fetchTopics(subjectId);
      fetchSubjects();
    } catch (err) { toast.error('Failed to delete topic'); }
  };

  const handleUpdateProgress = async (type, id, progress, subjectId) => {
    try {
      await axios.put(`${API}/teacher/curriculum/${type}/${id}/progress?progress=${progress}`);
      if (subjectId) fetchTopics(subjectId);
      fetchSubjects();
    } catch (err) { toast.error('Failed to update progress'); }
  };

  // --- Subtopic CRUD ---
  const handleSaveSubtopic = async () => {
    if (!subtopicForm.name) { toast.error('Subtopic name is required'); return; }
    try {
      await axios.post(`${API}/teacher/curriculum/topics/${activeTopicId}/subtopics`, subtopicForm);
      toast.success('Subtopic created');
      setShowSubtopicDialog(false);
      setSubtopicForm({ name: '', timeline_start: '', timeline_end: '', effort_hours: 0, priority: 'medium' });
      const topic = Object.values(topics).flat().find(t => t.id === activeTopicId);
      if (topic) fetchTopics(topic.subject_id);
    } catch (err) { toast.error('Failed to save subtopic'); }
  };

  const handleDeleteSubtopic = async (subtopicId, subjectId) => {
    try {
      await axios.delete(`${API}/teacher/curriculum/subtopics/${subtopicId}`);
      toast.success('Subtopic deleted');
      fetchTopics(subjectId);
    } catch (err) { toast.error('Failed to delete subtopic'); }
  };

  const handleSubtopicStatusConfirm = async (subtopicId, subjectId) => {
    const newStatus = subtopicStatusChanges[subtopicId];
    if (!newStatus) return;
    
    setUpdatingStatus(prev => ({ ...prev, [subtopicId]: true }));
    try {
      await axios.put(`${API}/teacher/curriculum/subtopics/${subtopicId}/status?status=${newStatus}`);
      toast.success(`Subtopic marked as ${newStatus.replace('_', ' ')}`);
      setSubtopicStatusChanges(prev => { const copy = { ...prev }; delete copy[subtopicId]; return copy; });
      fetchTopics(subjectId);
      fetchSubjects();
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [subtopicId]: false }));
    }
  };

  // --- Schedule ---
  const handleSaveSchedule = async () => {
    if (!scheduleForm.grade_level) { toast.error('Grade is required'); return; }
    try {
      await axios.post(`${API}/teacher/schedule`, scheduleForm);
      toast.success('Schedule saved');
      setShowScheduleDialog(false);
      fetchSchedules();
    } catch (err) { toast.error('Failed to save schedule'); }
  };

  const addDay = () => {
    setScheduleForm(prev => ({
      ...prev,
      schedule: [...prev.schedule, { day: '', slots: [{ start_time: '09:00', end_time: '10:00' }] }]
    }));
  };

  const addSlot = (dayIdx) => {
    const updated = [...scheduleForm.schedule];
    updated[dayIdx].slots.push({ start_time: '09:00', end_time: '10:00' });
    setScheduleForm(prev => ({ ...prev, schedule: updated }));
  };

  const updateDay = (idx, field, value) => {
    const updated = [...scheduleForm.schedule];
    if (field === 'day') updated[idx].day = value;
    setScheduleForm(prev => ({ ...prev, schedule: updated }));
  };

  const updateSlot = (dayIdx, slotIdx, field, value) => {
    const updated = [...scheduleForm.schedule];
    updated[dayIdx].slots[slotIdx][field] = value;
    setScheduleForm(prev => ({ ...prev, schedule: updated }));
  };

  const removeDay = (idx) => {
    setScheduleForm(prev => ({ ...prev, schedule: prev.schedule.filter((_, i) => i !== idx) }));
  };

  const removeSlot = (dayIdx, slotIdx) => {
    const updated = [...scheduleForm.schedule];
    updated[dayIdx].slots = updated[dayIdx].slots.filter((_, i) => i !== slotIdx);
    setScheduleForm(prev => ({ ...prev, schedule: updated }));
  };

  // --- AI Schedule ---
  const handleGenerateAISchedule = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/teacher/curriculum/generate-schedule`);
      setGeneratedSchedule(res.data.schedule);
      setShowAiScheduleDialog(true);
      toast.success('Schedule generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate schedule');
    } finally {
      setAiLoading(false);
    }
  };

  const toggleSubject = (id) => {
    if (expandedSubject === id) { setExpandedSubject(null); return; }
    setExpandedSubject(id);
    if (!topics[id]) fetchTopics(id);
  };

  // --- Summary Stats ---
  const totalTopics = subjects.reduce((a, s) => a + (s.topic_count || 0), 0);
  const avgProgress = subjects.length ? Math.round(subjects.reduce((a, s) => a + (s.progress || 0), 0) / subjects.length) : 0;
  const totalEffort = Object.values(topics).flat().reduce((a, t) => a + (t.effort_hours || 0), 0);

  return (
    <div className="space-y-6" data-testid="curriculum-management">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 border-l-4 border-l-blue-500">
          <p className="text-sm text-gray-500">Subjects</p>
          <p className="text-2xl font-bold" data-testid="subject-count">{subjects.length}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-emerald-500">
          <p className="text-sm text-gray-500">Topics</p>
          <p className="text-2xl font-bold">{totalTopics}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-amber-500">
          <p className="text-sm text-gray-500">Avg Progress</p>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold">{avgProgress}%</p>
            <Progress value={avgProgress} className="flex-1 h-2" />
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-purple-500">
          <p className="text-sm text-gray-500">Total Effort</p>
          <p className="text-2xl font-bold">{totalEffort}h</p>
        </Card>
      </div>

      {/* Action Bar */}
      <div className="flex flex-wrap gap-3">
        <Button data-testid="add-subject-btn" onClick={() => { setEditingSubject(null); setSubjectForm({ name: '', year: new Date().getFullYear().toString(), grade_level: '' }); setShowSubjectDialog(true); }} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-1" /> Add Subject
        </Button>
        <Button data-testid="manage-schedule-btn" variant="outline" onClick={() => setShowScheduleDialog(true)}>
          <CalendarDays className="w-4 h-4 mr-1" /> Class Schedule
        </Button>
        <Button data-testid="ai-schedule-btn" variant="outline" onClick={handleGenerateAISchedule} disabled={aiLoading}>
          {aiLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
          {aiLoading ? 'Generating...' : 'AI Schedule'}
        </Button>
      </div>

      {/* Subjects List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
      ) : subjects.length === 0 ? (
        <Card className="p-8 text-center text-gray-500">
          <p className="text-lg mb-2">No subjects yet</p>
          <p className="text-sm">Add your first subject to start building your curriculum.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {subjects.map(subject => (
            <Card key={subject.id} className="overflow-hidden" data-testid={`subject-card-${subject.id}`}>
              <div className="p-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => toggleSubject(subject.id)}>
                {expandedSubject === subject.id ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 truncate">{subject.name}</h3>
                    <Badge variant="outline" className="text-xs">{subject.grade_level}</Badge>
                    <Badge variant="outline" className="text-xs">{subject.year}</Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-gray-500">{subject.topic_count || 0} topics</span>
                    <Progress value={subject.progress || 0} className="w-24 h-1.5" />
                    <span className="text-xs font-medium text-gray-600">{subject.progress || 0}%</span>
                  </div>
                </div>
                <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" data-testid={`edit-subject-${subject.id}`} onClick={() => { setEditingSubject(subject); setSubjectForm({ name: subject.name, year: subject.year, grade_level: subject.grade_level }); setShowSubjectDialog(true); }}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteSubject(subject.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {expandedSubject === subject.id && (
                <div className="border-t bg-gray-50/50 p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-medium text-gray-600">Topics</h4>
                    <Button size="sm" data-testid={`add-topic-${subject.id}`} onClick={() => { setActiveSubjectId(subject.id); setEditingTopic(null); setTopicForm({ name: '', timeline_start: '', timeline_end: '', effort_hours: 0, priority: 'medium' }); setShowTopicDialog(true); }}>
                      <Plus className="w-3 h-3 mr-1" /> Add Topic
                    </Button>
                  </div>

                  {(topics[subject.id] || []).map(topic => (
                    <Card key={topic.id} className="bg-white" data-testid={`topic-card-${topic.id}`}>
                      <div className="p-3">
                        <div className="flex items-center gap-2">
                          <button className="p-0.5" onClick={() => setExpandedTopic(expandedTopic === topic.id ? null : topic.id)}>
                            {expandedTopic === topic.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                          <span className="font-medium text-sm flex-1">{topic.name}</span>
                          <Badge className={`text-xs ${PRIORITY_COLORS[topic.priority] || PRIORITY_COLORS.medium}`}>{topic.priority}</Badge>
                          {topic.effort_hours > 0 && (
                            <span className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" />{topic.effort_hours}h</span>
                          )}
                          <div className="flex items-center gap-1 w-32">
                            <input type="range" min="0" max="100" value={topic.progress || 0} className="w-full h-1.5 accent-blue-600"
                              onChange={e => handleUpdateProgress('topics', topic.id, parseInt(e.target.value), subject.id)} />
                            <span className="text-xs w-8 text-right">{topic.progress || 0}%</span>
                          </div>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setActiveSubjectId(subject.id); setEditingTopic(topic); setTopicForm({ name: topic.name, timeline_start: topic.timeline_start || '', timeline_end: topic.timeline_end || '', effort_hours: topic.effort_hours || 0, priority: topic.priority || 'medium' }); setShowTopicDialog(true); }}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteTopic(topic.id, subject.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        {(topic.timeline_start || topic.timeline_end) && (
                          <p className="text-xs text-gray-400 ml-7 mt-1">{topic.timeline_start || '?'} → {topic.timeline_end || '?'}</p>
                        )}
                      </div>

                      {expandedTopic === topic.id && (
                        <div className="border-t px-3 pb-3 pt-2 space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-medium text-gray-500">Subtopics ({topic.subtopics?.length || 0})</span>
                            <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => { setActiveTopicId(topic.id); setSubtopicForm({ name: '', timeline_start: '', timeline_end: '', effort_hours: 0, priority: 'medium' }); setShowSubtopicDialog(true); }}>
                              <Plus className="w-3 h-3 mr-1" /> Add
                            </Button>
                          </div>
                          {(topic.subtopics || []).map(sub => {
                            const currentStatus = subtopicStatusChanges[sub.id] || sub.status || 'not_started';
                            const hasChange = subtopicStatusChanges[sub.id] && subtopicStatusChanges[sub.id] !== (sub.status || 'not_started');
                            const STATUS_STYLES = {
                              not_started: 'bg-gray-50 border-gray-200',
                              in_progress: 'bg-amber-50 border-amber-200',
                              completed: 'bg-green-50 border-green-200'
                            };
                            return (
                              <div key={sub.id} className={`flex items-center gap-2 pl-4 py-2 text-sm rounded border ${STATUS_STYLES[currentStatus] || STATUS_STYLES.not_started}`} data-testid={`subtopic-${sub.id}`}>
                                <span className={`flex-1 ${currentStatus === 'completed' ? 'text-green-700 line-through' : 'text-gray-700'}`}>{sub.name}</span>
                                <Badge className={`text-xs ${PRIORITY_COLORS[sub.priority] || PRIORITY_COLORS.medium}`}>{sub.priority}</Badge>
                                {sub.effort_hours > 0 && <span className="text-xs text-gray-400">{sub.effort_hours}h</span>}
                                <Select
                                  value={currentStatus}
                                  onValueChange={v => setSubtopicStatusChanges(prev => ({ ...prev, [sub.id]: v }))}
                                >
                                  <SelectTrigger className="w-[130px] h-7 text-xs" data-testid={`status-select-${sub.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="not_started">Not Started</SelectItem>
                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                  </SelectContent>
                                </Select>
                                {hasChange && (
                                  <Button
                                    size="sm"
                                    className="h-7 px-2 bg-blue-600 hover:bg-blue-700 text-white"
                                    disabled={updatingStatus[sub.id]}
                                    onClick={() => handleSubtopicStatusConfirm(sub.id, subject.id)}
                                    data-testid={`confirm-status-${sub.id}`}
                                  >
                                    {updatingStatus[sub.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => handleDeleteSubtopic(sub.id, subject.id)}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Subject Dialog */}
      <Dialog open={showSubjectDialog} onOpenChange={setShowSubjectDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingSubject ? 'Edit Subject' : 'Add Subject'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Subject Name *</Label><Input data-testid="subject-name-input" value={subjectForm.name} onChange={e => setSubjectForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Mathematics" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Grade Level *</Label><Input data-testid="subject-grade-input" value={subjectForm.grade_level} onChange={e => setSubjectForm(p => ({ ...p, grade_level: e.target.value }))} placeholder="e.g. 10th" /></div>
              <div><Label>Year</Label><Input value={subjectForm.year} onChange={e => setSubjectForm(p => ({ ...p, year: e.target.value }))} placeholder="2026" /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-subject-btn" onClick={handleSaveSubject}>{editingSubject ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Topic Dialog */}
      <Dialog open={showTopicDialog} onOpenChange={setShowTopicDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingTopic ? 'Edit Topic' : 'Add Topic'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Topic Name *</Label><Input data-testid="topic-name-input" value={topicForm.name} onChange={e => setTopicForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Algebra" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Start Date</Label><Input type="date" value={topicForm.timeline_start} onChange={e => setTopicForm(p => ({ ...p, timeline_start: e.target.value }))} /></div>
              <div><Label>End Date</Label><Input type="date" value={topicForm.timeline_end} onChange={e => setTopicForm(p => ({ ...p, timeline_end: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Effort (hours)</Label><Input type="number" min="0" step="0.5" value={topicForm.effort_hours} onChange={e => setTopicForm(p => ({ ...p, effort_hours: parseFloat(e.target.value) || 0 }))} /></div>
              <div>
                <Label>Priority</Label>
                <Select value={topicForm.priority} onValueChange={v => setTopicForm(p => ({ ...p, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-topic-btn" onClick={handleSaveTopic}>{editingTopic ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subtopic Dialog */}
      <Dialog open={showSubtopicDialog} onOpenChange={setShowSubtopicDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Subtopic</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Subtopic Name *</Label><Input data-testid="subtopic-name-input" value={subtopicForm.name} onChange={e => setSubtopicForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Linear Equations" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Start Date</Label><Input type="date" value={subtopicForm.timeline_start} onChange={e => setSubtopicForm(p => ({ ...p, timeline_start: e.target.value }))} /></div>
              <div><Label>End Date</Label><Input type="date" value={subtopicForm.timeline_end} onChange={e => setSubtopicForm(p => ({ ...p, timeline_end: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Effort (hours)</Label><Input type="number" min="0" step="0.5" value={subtopicForm.effort_hours} onChange={e => setSubtopicForm(p => ({ ...p, effort_hours: parseFloat(e.target.value) || 0 }))} /></div>
              <div>
                <Label>Priority</Label>
                <Select value={subtopicForm.priority} onValueChange={v => setSubtopicForm(p => ({ ...p, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-subtopic-btn" onClick={handleSaveSubtopic}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Class Schedule</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Grade Level *</Label><Input value={scheduleForm.grade_level} onChange={e => setScheduleForm(p => ({ ...p, grade_level: e.target.value }))} placeholder="e.g. 10th" /></div>
              <div><Label>Year</Label><Input value={scheduleForm.year} onChange={e => setScheduleForm(p => ({ ...p, year: e.target.value }))} /></div>
            </div>

            <div className="space-y-3">
              {scheduleForm.schedule.map((day, dIdx) => (
                <Card key={dIdx} className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Select value={day.day} onValueChange={v => updateDay(dIdx, 'day', v)}>
                      <SelectTrigger className="w-40"><SelectValue placeholder="Select day" /></SelectTrigger>
                      <SelectContent>
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={() => addSlot(dIdx)}>+ Slot</Button>
                    <Button size="sm" variant="ghost" className="text-red-500 ml-auto" onClick={() => removeDay(dIdx)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  {day.slots.map((slot, sIdx) => (
                    <div key={sIdx} className="flex items-center gap-2 pl-4">
                      <Input type="time" value={slot.start_time} className="w-32" onChange={e => updateSlot(dIdx, sIdx, 'start_time', e.target.value)} />
                      <span className="text-gray-400">→</span>
                      <Input type="time" value={slot.end_time} className="w-32" onChange={e => updateSlot(dIdx, sIdx, 'end_time', e.target.value)} />
                      <Button size="sm" variant="ghost" className="text-red-400 h-7 w-7 p-0" onClick={() => removeSlot(dIdx, sIdx)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </Card>
              ))}
              <Button variant="outline" className="w-full" onClick={addDay}><Plus className="w-4 h-4 mr-1" /> Add Day</Button>
            </div>

            {schedules.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium mb-2">Saved Schedules</h4>
                {schedules.map(s => (
                  <div key={s.id} className="text-sm text-gray-600 flex gap-2 items-center">
                    <Badge variant="outline">{s.grade_level}</Badge>
                    <span>{s.year}</span>
                    <span className="text-gray-400">— {s.schedule?.length || 0} days</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter><Button data-testid="save-schedule-btn" onClick={handleSaveSchedule}>Save Schedule</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generated Schedule Dialog */}
      <Dialog open={showAiScheduleDialog} onOpenChange={setShowAiScheduleDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> AI-Generated Teaching Schedule</DialogTitle></DialogHeader>
          {generatedSchedule && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-3 text-center">
                  <p className="text-sm text-gray-500">Weekly Hours</p>
                  <p className="text-xl font-bold">{generatedSchedule.total_hours_per_week || '—'}</p>
                </Card>
                <Card className="p-3 text-center">
                  <p className="text-sm text-gray-500">Est. Weeks to Complete</p>
                  <p className="text-xl font-bold">{generatedSchedule.estimated_completion_weeks || '—'}</p>
                </Card>
              </div>

              {(generatedSchedule.weekly_plan || []).map((day, idx) => (
                <Card key={idx} className="p-3">
                  <h4 className="font-semibold text-blue-700 mb-2">{day.day}</h4>
                  <div className="space-y-2">
                    {(day.sessions || []).map((session, sIdx) => (
                      <div key={sIdx} className="flex items-start gap-3 text-sm bg-gray-50 rounded p-2">
                        <span className="font-mono text-xs text-gray-500 whitespace-nowrap pt-0.5">{session.time_slot}</span>
                        <div>
                          <span className="font-medium">{session.subject}</span>
                          <span className="text-gray-500"> — {session.topic}</span>
                          {session.subtopic && <span className="text-gray-400"> / {session.subtopic}</span>}
                          {session.activity && <p className="text-xs text-gray-400 mt-0.5">{session.activity}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}

              {generatedSchedule.recommendations && (
                <Card className="p-3">
                  <h4 className="font-semibold text-sm mb-2">Recommendations</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {generatedSchedule.recommendations.map((r, i) => <li key={i}>• {r}</li>)}
                  </ul>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CurriculumManagement;
